"use client";
import * as React from "react";
import { useState, useEffect } from "react";

type Keys = keyof typeof INITIAL_VALUES.oidcConnection;
type Values = (typeof INITIAL_VALUES.oidcConnection)[Keys];
import type {
  CreateConnectionProps,
  FormObj,
  OIDCSSOConnection,
} from "../../types";
import { saveConnection } from "../../utils";
import defaultClasses from "./index.module.css";
import cssClassAssembler from "../../../utils/cssClassAssembler";
import Button from "../../../../shared/Button/index";
import Spacer from "../../../../shared/Spacer/index";
import Separator from "../../../../shared/Separator/index";
import Anchor from "../../../../shared/Anchor/index";
import InputField from "../../../../shared/inputs/InputField/index";
import SecretInputFormControl from "../../../../shared/inputs/SecretInputFormControl/index";
import Select from "../../../../shared/Select/index";
import ItemList from "../../../../shared/inputs/ItemList/index";
const DEFAULT_VALUES = {
  variant: "basic",
} satisfies Partial<CreateConnectionProps>;
const INITIAL_VALUES = {
  oidcConnection: {
    name: "",
    label: "",
    description: "",
    tenant: "",
    product: "",
    redirectUrl: [""],
    defaultRedirectUrl: "",
    oidcClientSecret: "",
    oidcClientId: "",
    oidcDiscoveryUrl: "",
    "oidcMetadata.issuer": "",
    "oidcMetadata.authorization_endpoint": "",
    "oidcMetadata.token_endpoint": "",
    "oidcMetadata.jwks_uri": "",
    "oidcMetadata.userinfo_endpoint": "",
    sortOrder: "" as unknown as string | number,
  },
};

function CreateOIDCConnection(props: CreateConnectionProps) {
  const [oidcConnection, setOidcConnection] = useState(
    () => INITIAL_VALUES.oidcConnection
  );

  const [isSaving, setIsSaving] = useState(() => false);

  function updateConnection(data: Partial<OIDCSSOConnection>) {
    return {
      ...oidcConnection,
      ...data,
    };
  }

  function handleChange(event: Event) {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement;
    const id = target.id as Keys;
    const targetValue = (
      event.currentTarget as HTMLInputElement | HTMLTextAreaElement
    )?.value as Values;
    setOidcConnection(
      updateConnection({
        [id]: targetValue,
      })
    );
  }

  function handleItemListUpdate(fieldName: string, listValue: string[]) {
    setOidcConnection(
      updateConnection({
        [fieldName]: listValue,
      })
    );
  }

  function save(event: Event) {
    event.preventDefault();
    const formObj = {} as any;
    Object.entries(oidcConnection).map(([key, val]) => {
      if (key.startsWith("oidcMetadata.")) {
        if (formObj.oidcMetadata === undefined) {
          formObj.oidcMetadata = {} as Exclude<
            OIDCSSOConnection["oidcMetadata"],
            undefined
          >;
        }
        formObj.oidcMetadata[key.replace("oidcMetadata.", "")] = val;
      } else if (key === "sortOrder") {
        // pass sortOrder only if set to non-empty string
        val !== "" && (formObj[key] = +val); // convert sortOrder into number
      } else {
        formObj[key] = val;
      }
    });
    setIsSaving(true);
    saveConnection({
      url: props.urls.post,
      formObj: formObj as FormObj,
      connectionIsOIDC: true,
      callback: async (data) => {
        setIsSaving(false);
        if (data && typeof data === "object") {
          if ("error" in data) {
            typeof props.errorCallback === "function" &&
              props.errorCallback(data.error.message);
          } else {
            typeof props.successCallback === "function" &&
              props.successCallback({
                operation: "CREATE",
                connection: data,
                connectionIsOIDC: true,
              });
          }
        }
      },
    });
  }

  function classes() {
    return {
      form: cssClassAssembler(props.classNames?.form, defaultClasses.form),
      inputField: {
        label: props.classNames?.label,
        input: props.classNames?.input,
        container: props.classNames?.fieldContainer,
      },
      select: {
        label: props.classNames?.label,
        select: props.classNames?.select,
      },
      textarea: {
        label: props.classNames?.label,
        textarea: props.classNames?.textarea,
        container: props.classNames?.fieldContainer,
      },
    };
  }

  function formVariant() {
    return props.variant || DEFAULT_VALUES.variant;
  }

  function isExcluded(fieldName: keyof OIDCSSOConnection) {
    return !!(props.excludeFields as (keyof OIDCSSOConnection)[])?.includes(
      fieldName
    );
  }

  function isReadOnly(fieldName: keyof OIDCSSOConnection) {
    if (
      fieldName === "tenant" &&
      Array.isArray(props.defaults?.tenant) &&
      props.defaults.tenant.length === 1
    ) {
      return true;
    }
    return !!(props.readOnlyFields as (keyof OIDCSSOConnection)[])?.includes(
      fieldName
    );
  }

  function isTenantADropdown() {
    return (
      Array.isArray(props.defaults?.tenant) && props.defaults.tenant.length > 1
    );
  }

  function tenantOptions() {
    return Array.isArray(props.defaults?.tenant)
      ? props.defaults?.tenant.map((tenant: string) => ({
          value: tenant,
          text: tenant,
        }))
      : [];
  }

  function shouldDisplayHeader() {
    if (props.displayHeader !== undefined) {
      return props.displayHeader;
    }
    return true;
  }

  useEffect(() => {
    if (props.defaults) {
      // forceAuthn is a SAML only setting, remove it
      const { forceAuthn, tenant, ...rest } = props.defaults;
      const _tenant = Array.isArray(tenant) ? tenant[0] : tenant;
      setOidcConnection(
        updateConnection({
          ...rest,
          tenant: _tenant,
        })
      );
    }
  }, [props.defaults]);

  return (
    <div>
      {shouldDisplayHeader() ? (
        <h5 className={defaultClasses.h5}>Create SSO Connection</h5>
      ) : null}
      <form
        method="post"
        onSubmit={(event) => save(event)}
        className={classes().form}
      >
        {formVariant() === "advanced" ? (
          <>
            {!isExcluded("name") ? (
              <>
                <InputField
                  label="Connection name (Optional)"
                  id="name"
                  placeholder="MyApp"
                  classNames={classes().inputField}
                  required={false}
                  readOnly={isReadOnly("name")}
                  value={oidcConnection.name}
                  handleInputChange={handleChange}
                />
                <Spacer y={6} />
              </>
            ) : null}
            {!isExcluded("label") ? (
              <>
                <InputField
                  label="Connection label (Optional)"
                  id="label"
                  placeholder="An internal label to identify the connection"
                  classNames={classes().inputField}
                  required={false}
                  readOnly={isReadOnly("label")}
                  value={oidcConnection.label}
                  handleInputChange={handleChange}
                />
                <Spacer y={6} />
              </>
            ) : null}
            {!isExcluded("description") ? (
              <>
                <InputField
                  label="Description (Optional)"
                  id="description"
                  placeholder="A short description not more than 100 characters"
                  classNames={classes().inputField}
                  required={false}
                  readOnly={isReadOnly("description")}
                  maxLength={100}
                  value={oidcConnection.description}
                  handleInputChange={handleChange}
                />
                <Spacer y={6} />
              </>
            ) : null}
            {!isExcluded("tenant") ? (
              <>
                {!isTenantADropdown() ? (
                  <InputField
                    label="Tenant"
                    id="tenant"
                    placeholder="acme.com"
                    aria-describedby="tenant-hint"
                    classNames={classes().inputField}
                    required
                    readOnly={isReadOnly("tenant")}
                    value={oidcConnection.tenant}
                    handleInputChange={handleChange}
                  />
                ) : null}
                {isTenantADropdown() ? (
                  <div className={defaultClasses.selectContainer}>
                    <Select
                      label="Tenant"
                      name="tenant"
                      id="tenant"
                      options={tenantOptions()}
                      classNames={classes().select}
                      selectedValue={oidcConnection.tenant}
                      handleChange={handleChange}
                    />
                  </div>
                ) : null}
                <div id="tenant-hint" className={defaultClasses.hint}>
                  Unique identifier for the tenant to which this SSO connection
                  is linked.See
                  <Spacer x={1} />
                  <Anchor
                    href="https://boxyhq.com/guides/jackson/configuring-saml-sso#sso-connection-identifier"
                    linkText="SSO connection identifier."
                  />
                </div>
                <Spacer y={6} />
              </>
            ) : null}
            {!isExcluded("product") ? (
              <>
                <InputField
                  label="Product"
                  id="product"
                  placeholder="demo"
                  aria-describedby="product-hint"
                  classNames={classes().inputField}
                  required
                  readOnly={isReadOnly("product")}
                  value={oidcConnection.product}
                  handleInputChange={handleChange}
                />
                <div id="product-hint" className={defaultClasses.hint}>
                  Identifies the product/app to which this SSO connection is
                  linked.
                </div>
                <Spacer y={6} />
              </>
            ) : null}
            {!isExcluded("redirectUrl") ? (
              <>
                <ItemList
                  inputType="url"
                  label="Allowed redirect URLs"
                  fieldName="redirectUrl"
                  currentlist={oidcConnection.redirectUrl}
                  handleItemListUpdate={handleItemListUpdate}
                  classNames={classes().inputField}
                />
                <div id="redirectUrl-hint" className={defaultClasses.hint}>
                  URL(s) to redirect the user to after login. Only the URLs in
                  this list are allowed in the OAuth flow.
                </div>
                <Spacer y={6} />
              </>
            ) : null}
            {!isExcluded("defaultRedirectUrl") ? (
              <>
                <InputField
                  label="Default redirect URL"
                  id="defaultRedirectUrl"
                  aria-describedby="defaultRedirectUrl-hint"
                  placeholder="http://localhost:3366"
                  type="url"
                  classNames={classes().inputField}
                  required
                  readOnly={isReadOnly("defaultRedirectUrl")}
                  value={oidcConnection.defaultRedirectUrl}
                  handleInputChange={handleChange}
                />
                <div
                  id="defaultRedirectUrl-hint"
                  className={defaultClasses.hint}
                >
                  URL to redirect the user to after an IdP initiated login.
                </div>
                <Spacer y={6} />
              </>
            ) : null}
            <Separator text="OIDC Provider Metadata" />
            <Spacer y={6} />
          </>
        ) : null}
        <InputField
          label="Client ID"
          id="oidcClientId"
          aria-describedby="oidc-clientid-hint"
          classNames={classes().inputField}
          value={oidcConnection.oidcClientId}
          handleInputChange={handleChange}
        />
        <div id="oidc-clientid-hint" className={defaultClasses.hint}>
          ClientId of the app created on the OIDC Provider.
        </div>
        <Spacer y={6} />
        <SecretInputFormControl
          label="Client Secret"
          id="oidcClientSecret"
          aria-describedby="oidc-clientsecret-hint"
          readOnly={false}
          classNames={classes().inputField}
          handleChange={handleChange}
          value={oidcConnection.oidcClientSecret}
          required
        />
        <div id="oidc-clientsecret-hint" className={defaultClasses.hint}>
          ClientSecret of the app created on the OIDC Provider.
        </div>
        <Spacer y={6} />
        <InputField
          id="oidcDiscoveryUrl"
          type="url"
          label="Well-known URL of OpenID Provider"
          placeholder="https://example.com/.well-known/openid-configuration"
          aria-describedby="oidc-metadata-hint"
          classNames={classes().inputField}
          value={oidcConnection.oidcDiscoveryUrl}
          handleInputChange={handleChange}
        />
        <div id="oidc-metadata-hint" className={defaultClasses.hint}>
          Enter the well known discovery path of OpenID provider or manually
          enter the OpenId provider metadata below.
        </div>
        <Spacer y={6} />
        <Separator text="OR" />
        <Spacer y={6} />
        <InputField
          id="oidcMetadata.issuer"
          label="Issuer"
          placeholder="https://example.com"
          classNames={classes().inputField}
          value={oidcConnection["oidcMetadata.issuer"]}
          handleInputChange={handleChange}
        />
        <Spacer y={6} />
        <InputField
          id="oidcMetadata.authorization_endpoint"
          type="url"
          label="Authorization Endpoint"
          placeholder="https://example.com/oauth/authorize"
          classNames={classes().inputField}
          value={oidcConnection["oidcMetadata.authorization_endpoint"]}
          handleInputChange={handleChange}
        />
        <Spacer y={6} />
        <InputField
          id="oidcMetadata.token_endpoint"
          type="url"
          label="Token endpoint"
          placeholder="https://example.com/oauth/token"
          classNames={classes().inputField}
          value={oidcConnection["oidcMetadata.token_endpoint"]}
          handleInputChange={handleChange}
        />
        <Spacer y={6} />
        <InputField
          id="oidcMetadata.jwks_uri"
          type="url"
          label="JWKS URI"
          placeholder="https://example.com/.well-known/jwks.json"
          classNames={classes().inputField}
          value={oidcConnection["oidcMetadata.jwks_uri"]}
          handleInputChange={handleChange}
        />
        <Spacer y={6} />
        <InputField
          id="oidcMetadata.userinfo_endpoint"
          type="url"
          label="UserInfo endpoint"
          placeholder="https://example.com/userinfo"
          autoComplete="one-time-code"
          classNames={classes().inputField}
          value={oidcConnection["oidcMetadata.userinfo_endpoint"]}
          handleInputChange={handleChange}
        />
        <Spacer y={6} />
        {formVariant() === "advanced" ? (
          <>
            {!isExcluded("sortOrder") ? (
              <>
                <InputField
                  label="Sort Order"
                  id="sortOrder"
                  type="number"
                  min="0"
                  placeholder="10"
                  classNames={classes().inputField}
                  readOnly={isReadOnly("sortOrder")}
                  value={oidcConnection.sortOrder as string}
                  handleInputChange={handleChange}
                />
                <div id="sortOrder-hint" className={defaultClasses.hint}>
                  Connections will be sorted (in a listing view like IdP
                  Selection) using this setting. Higher values will be displayed
                  first.
                </div>
              </>
            ) : null}
          </>
        ) : null}
        <Spacer y={6} />
        <div className={defaultClasses.formAction}>
          {typeof props.cancelCallback === "function" ? (
            <Button
              type="button"
              name="Cancel"
              variant="outline"
              handleClick={props.cancelCallback}
              classNames={props.classNames?.button?.cancel}
            />
          ) : null}
          <Button
            type="submit"
            name="Save"
            classNames={props.classNames?.button?.ctoa}
            isLoading={isSaving}
          />
        </div>
      </form>
    </div>
  );
}

export default CreateOIDCConnection;
